<template>
  <slice
    :id="block._key"
    class="slice--alert-subscribe container"
    :class="{
      'image-in-screen': imageInScreen,
      'image-after': block.image_after_text,
      'side-by-side': block.side_by_side,
      'no-image': !hasImage,
      'is-fixed': block.sticky && fixed,
    }"
    @in="onWaypoint"
    @out="onWaypoint"
    @click="click"
  >
    <div v-if="productOnSell">
      <div
        v-if="block.linked_product_render_format === 'cards'"
        class="row-md justify-center"
      >
        <div
          v-for="params in productColors"
          :key="params.color"
          class="col-md-3 ml-md-1 mr-md-1 mb-1 mb-md-0"
        >
          <product-card
            :uid="linkedProduct.uid"
            :name="linkedProduct.name"
            :image="params.image ? params.image : linkedProduct.image"
            :product-status="linkedProduct.status"
            :price="linkedShopifyProduct.price"
            :inventory="linkedShopifyProduct.totalInventory"
            :compare-at-price="linkedShopifyProduct.compareAtPrice"
            :button-label="linkedProduct.buttonLabel"
            :shopify-id="linkedProduct.shopifyId"
            :params="params"
          />
        </div>
      </div>
      <product-card-highlight
        v-else
        :uid="linkedProduct.uid"
        :name="linkedProduct.name"
        :image="linkedProduct.image"
        :image-shadow="block.linked_product_shadow"
        :product-status="linkedProduct.status"
        :price="linkedShopifyProduct.price"
        :inventory="linkedShopifyProduct.totalInventory"
        :compare-at-price="linkedShopifyProduct.compareAtPrice"
        :button-label="linkedProduct.buttonLabel"
        :shopify-id="linkedProduct.shopifyId"
        :colors="linkedProduct.colors"
      />
    </div>
    <div
      v-else
      :class="{
        'row-md row-no-padding justify-center': block.side_by_side,
      }"
    >
      <div
        v-if="hasImage"
        class="slice__image"
        :class="{
          'row-md justify-center': !block.side_by_side,
          'col-md-5': block.side_by_side,
        }"
      >
        <div v-if="block.side_by_side">
          <lazy-image
            :image="image"
            sizes="720px"
            provider="sanity"
            :widths="[390, 720, 1440]"
            role="presentation"
          />
        </div>
        <div v-else class="col-md-8">
          <lazy-image
            :image="image"
            sizes="960px"
            provider="sanity"
            class="d-none d-md-block"
            role="presentation"
          />
          <lazy-image
            :image="imageMobile"
            :widths="[320, 414, 640, 769]"
            provider="sanity"
            class="d-md-none"
            role="presentation"
          />
        </div>
      </div>

      <div
        class="slice__inner"
        :class="{
          'row-md justify-center': !block.side_by_side,
          'col-md-5 d-flex': block.side_by_side,
        }"
      >
        <div
          :class="{
            'col-md-7': !block.side_by_side,
            'flex-grow-1 d-flex': block.side_by_side,
          }"
        >
          <div
            class="slice--alert-subscribe__inner"
            :class="{
              'flex-grow-1 d-flex align-center': block.side_by_side,
            }"
          >
            <create-alert
              v-if="product"
              :id="`alert-${index}`"
              :product="{
                productSlug: product.uid,
                productName: product.data.product_name[0].text,
                productId: product.data.shopify_id,
              }"
              :title="block.available_at"
              :text="explanations"
              :origin="ORIGIN.ARTICLE"
            />
            <create-alert
              v-else
              :id="`alert-${index}`"
              :product="{
                productSlug: block.subscribe_to,
                productName: '',
                productId: '',
              }"
              :title="block.available_at"
              :text="explanations"
              :origin="ORIGIN.ARTICLE"
            />
          </div>
        </div>
      </div>
    </div>
  </slice>
</template>

<script>
import NuxtSSRScreenSize from 'nuxt-ssr-screen-size'

import Slice from '@/components/Slices/Slice'
import LazyImage from '@/components/Image/LazyImage'
import CreateAlert from '@/components/Product/CreateAlert'

import { ORIGIN, STATUS } from '@/const'
import { getSanityBunnyImageLink } from '@/utils/image'

export default {
  name: 'BlockSubscribe',
  components: {
    Slice,
    LazyImage,
    CreateAlert,
    ProductCard: () => ({
      component: import(
        '@/components/Product/Card' /* webpackChunkName: 'product_card' */
      ),
    }),
    ProductCardHighlight: () => ({
      component: import(
        '@/components/Product/CardHighlight' /* webpackChunkName: 'product_card_highlight' */
      ),
    }),
  },
  mixins: [NuxtSSRScreenSize.NuxtSSRScreenSizeMixin],
  props: {
    /**
     * Le block fournit par l'object Sanity
     */
    block: { type: Object, required: true },
    /**
     * Le positionnement du slice dans la liste
     */
    index: { type: Number, default: 0 },
    /**
     * Le produit lié
     */
    linkedProduct: { type: Object, default: null },
    /**
     * Le produit shopify lié
     */
    linkedShopifyProduct: { type: Object, default: null },
  },
  data() {
    return {
      product: null,
      ORIGIN,
      imageInScreen: false,
      fixed: false,
    }
  },
  computed: {
    hasImage() {
      return this.block.image && this.block.image.image
    },
    image() {
      if (this.hasImage) {
        if (this.block.side_by_side) {
          return {
            url: getSanityBunnyImageLink(this.block.image, {
              width: 720,
              height: 720,
            }),
            dimensions: {
              width: 720,
              height: 720,
            },
          }
        } else {
          return {
            url: getSanityBunnyImageLink(this.block.image, {
              width: 960,
              height: 400,
            }),
            dimensions: {
              width: 960,
              height: 400,
            },
          }
        }
      }

      return null
    },
    imageMobile() {
      if (this.hasImage) {
        return {
          url: getSanityBunnyImageLink(this.block.image, {
            width: 375,
            height: 250,
          }),
          dimensions: {
            width: 375,
            height: 250,
          },
        }
      }

      return null
    },
    productOnSell() {
      return (
        this.linkedProduct &&
        (this.linkedProduct.status === STATUS.PRECOMMANDE ||
          this.linkedProduct.status === STATUS.STOCK)
      )
    },
    productColors() {
      if (this.linkedProduct) {
        return this.linkedProduct.colors
          .map((c) => ({
            color: c.color_name,
            image: c.image_principale.list,
          }))
          .slice(0, 3)
      }
      return []
    },
    explanations() {
      if (this.block.explanations && this.block.explanations.length > 0) {
        return this.block.explanations.reduce((acc, curr) => {
          return (
            acc +
            curr.children.reduce((acc, curr) => {
              return acc + curr.text
            }, '')
          )
        }, '')
      }

      return ''
    },
  },
  beforeDestroy() {
    document.body.classList.remove('create-alert--fixed')
  },
  methods: {
    onWaypoint({ going, el }) {
      if (going === this.$waypointMap.GOING_IN) {
        this.imageInScreen = true

        this.fixed = false
        this.$el.style.height = ''
        document.body.classList.remove('create-alert--fixed')
      } else {
        const h = document.getElementById('header')
        if (h) {
          const header = h.firstChild
          const screenY = window.scrollY - header.offsetHeight

          this.$el.style.height = `${this.$el.offsetHeight}px`
          this.fixed = screenY > el.offsetTop
          if (this.block.sticky) {
            document.body.classList.add('create-alert--fixed')
          }
        }
      }

      if (going === this.$waypointMap.GOING_OUT) {
        this.imageInScreen = false
      }
    },
    click() {
      if (this.block.sticky && this.fixed) {
        if (this.$vssWidth <= 769) {
          this.$el.scrollIntoView()
        }
      }
    },
  },
}
</script>

<style lang="scss">
.slice--alert-subscribe {
  &:not(.side-by-side) {
    .slice__inner {
      margin-top: rem(-100px);
    }
  }

  &__inner {
    position: relative;
    background: var(--secondary-color);
    color: var(--tertiary-color);
    padding: rem($spacing * 2) rem($spacing);
    text-align: center;
    z-index: 1;

    @include mq($from: tablet) {
      padding: calc(var(--spacing) * 4) calc(var(--spacing) * 6);
      margin: 0 rem($spacing);
    }
  }

  .lazy-image-container {
    transition: all 0.3s;
  }

  .create-alert {
    &__title,
    &__desc {
      @include mq($from: tablet) {
        margin-bottom: rem($spacing * 2);
      }
    }

    &__title {
      font-size: var(--h3-font-size);
      line-height: 1.05;
      margin-bottom: var(--spacing);
    }

    &__desc {
      font-size: var(--base-font-size);
    }

    form {
      @include mq($from: tablet) {
        height: rem(50px);
      }
    }

    .action {
      white-space: nowrap;

      @include mq($from: tablet) {
        margin-left: rem($spacing);
        padding-top: 0;
        padding-bottom: 0;
        height: 100%;
      }

      &--light {
        box-shadow: none;
      }

      @include on-hover-and-focus {
        background: $yellow;
        color: var(--secondary-color);
        box-shadow: none;
      }

      @include mq($until: tablet) {
        width: 100%;
      }
    }

    .input__line {
      background: $yellow;
      left: 0;
      bottom: 0;
      transform: none;
      height: rem(2px);
    }

    @include mq($from: tablet) {
      form {
        display: flex;
        justify-content: center;
        align-items: stretch;
      }

      .input {
        flex: 1 0;
        margin-bottom: 0;

        input {
          padding-left: rem($spacing * 2);
        }
      }

      .input__container {
        border-bottom: 0;
      }

      .input__container,
      input[type='email'] {
        height: 100%;
      }
    }
  }

  &.image-in-screen {
    .lazy-image-container {
      @include mq($until: tablet) {
        margin-left: rem(-$spacing);
        margin-right: rem(-$spacing);
      }
    }
  }

  &.image-after {
    .slice__inner {
      order: 1;
    }

    .slice__image {
      order: 2;
    }

    &:not(.side-by-side) {
      > div {
        display: flex;
        flex-direction: column;
      }

      .slice__inner {
        position: relative;
        z-index: 1;
        margin-top: 0;
      }

      .slice__image {
        margin-top: rem(-100px);
      }
    }
  }

  &.no-image {
    .slice--alert-subscribe__inner {
      margin: 0;
    }

    .slice__inner {
      margin-top: 0;
    }
  }

  @keyframes appear-top {
    0% {
      opacity: 0;
      transform: translateY(-100%);
    }

    100% {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes appear-bottom {
    0% {
      opacity: 0;
      transform: translateY(100%);
    }

    100% {
      opacity: 1;
      transform: translateY(0);
    }
  }

  &.side-by-side {
    .slice--alert-subscribe__inner {
      margin: 0;
    }

    .create-alert {
      form {
        @include mq($from: tablet) {
          height: rem(45px);
        }
      }
    }

    @include mq($until: tablet) {
      .slice__inner {
        margin: 0 calc(var(--spacing) * -1);
      }
    }
  }

  &.is-fixed {
    .slice--alert-subscribe__inner {
      position: fixed;
      left: 0;
      right: 0;
      padding: var(--spacing);
      animation: appear-bottom 0.3s;
      z-index: 3000;

      @include mq($until: tablet) {
        bottom: 0;
      }

      @include mq($from: tablet) {
        top: 0;
        padding: calc(var(--spacing) * 0.2) var(--spacing);
        animation: appear-top 0.3s;
        transition: margin 0.4s cubic-bezier(0.46, 0.01, 0.32, 1);

        .header-visible & {
          margin-top: rem(50px);

          @include mq($from: tablet) {
            margin-top: rem(78px);
          }
        }
      }
    }

    .create-alert {
      @include mq($from: tablet) {
        display: flex;
        justify-content: center;
        align-items: center;
      }

      &__title {
        font-size: var(--h4-font-size);
        margin-bottom: 0;

        @include mq($from: tablet) {
          font-size: var(--h5-font-size);
          margin-right: calc(var(--spacing) * 2);
        }
      }

      &__desc {
        display: none;

        @include mq($until: tablet) {
          font-size: var(--caption-font-size);
        }
      }

      form {
        height: auto;

        @include mq($until: tablet) {
          display: none;
        }

        @include mq($from: tablet) {
          height: rem(40px);
        }
      }

      input {
        padding-left: var(--spacing);
      }

      .input {
        @include mq($until: tablet) {
          margin-bottom: calc(var(--spacing) * 0.4);
        }
      }

      .input__error {
        text-align: left;
        padding: calc(var(--spacing) * 0.5) var(--spacing);
        margin: 0;
      }

      .action,
      .action__inner {
        height: 100%;
      }

      .action {
        @include mq($from: tablet) {
          margin-left: calc(var(--spacing) * 0.6);
        }
      }
    }
  }
}
</style>
